import './index.scss'

import { useState } from 'react'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'

import ErrorView from '@components/ErrorView'
import PageHeader from '@components/PageHeader'
import LoadingIndicator from '@components/LoadingIndicator'
import useTrainingModule from '@hooks/useTrainingModule'
import { TrainingModule, TrainingResource, TrainingVideo } from '@interfaces/training.interface'
import ModuleEditorModal from '@pages/v2/Volunteering/ModuleEditorModal'
import LessonEditorModal from './LessonEditorModal'

import lessonPlaceholder from 'url:@assets/images/NotesPlaceholder.png';
import bookIcon from 'url:@assets/icons/book.svg';
import pencilIcon from 'url:@assets/icons/pencil.svg';
import plusIcon from 'url:@assets/icons/plusNoCircle.svg';

export default function TrainingModulePage() {
  const navigate = useNavigate();
  const { t } = useTranslation('trainingModule');
  const location = useLocation();
  const pathParams = useParams();
  const moduleID = pathParams.moduleID;
  const routedModule = (location.state as { module?: TrainingModule } | null)?.module ?? null;

  // Seeded from navigation state so the header renders immediately; the fetch
  // brings the Lessons with it.
  const { module, resources: lessons, loading, hasLoaded, error, refetch } = useTrainingModule(moduleID, routedModule);

  // The module itself can come from navigation state, so the Lessons list has
  // its own loading state rather than riding on the page-level one.
  const isLoadingLessons = loading && !hasLoaded;

  const [lessonForEditor, setLessonForEditor] = useState<TrainingResource | null>(null);
  const [isLessonEditorOpen, setIsLessonEditorOpen] = useState(false);
  const [isModuleEditorOpen, setIsModuleEditorOpen] = useState(false);

  const openNewLessonEditor = () => {
    setLessonForEditor(null);
    setIsLessonEditorOpen(true);
  };

  const closeLessonEditor = () => {
    setIsLessonEditorOpen(false);

    window.setTimeout(() => {
      setLessonForEditor(null);
    }, 200);
  };

  if (!moduleID) {
    return <Navigate to='/volunteering/training' replace />
  }

  if (!module && loading && !hasLoaded) {
    return <div className='training-module-page training-state'>
      <LoadingIndicator />
    </div>
  }

  if (error) {
    return <ErrorView
      error={error}
      buttonText={t('retry')}
      buttonAction={() => refetch()}
      fullHeight />
  }

  if (!module) {
    return <ErrorView
      error={t('notFound')}
      buttonText={t('backToTraining')}
      buttonAction={() => navigate('/volunteering/training')}
      fullHeight />
  }

  return <div className='training-module-page'>
    <PageHeader
      title={module.attributes.name}
      subtitle={module.attributes.description ?? undefined}
      backLocation='/volunteering/training'
      links={[
        {
          icon: pencilIcon,
          title: t('edit'),
          onClick: () => setIsModuleEditorOpen(true)
        }
      ]} />

    <ModuleEditorModal
      key={`edit-module-${module.id}`}
      isOpen={isModuleEditorOpen}
      module={module}
      close={() => setIsModuleEditorOpen(false)}
      onSaved={() => {
        setIsModuleEditorOpen(false);
        void refetch();
      }}
    />

    <LessonEditorModal
      key={lessonForEditor?.id ?? 'lesson-editor'}
      isOpen={isLessonEditorOpen}
      moduleID={moduleID}
      lesson={lessonForEditor}
      close={closeLessonEditor}
      onSaved={() => {
        closeLessonEditor();
        void refetch();
      }}
    />

    <section className='training-lessons'>
      {isLoadingLessons ? <div className='training-state'>
        <LoadingIndicator fullScreen={false} />
      </div> : <>
      {lessons.length > 0 ? <div className='lessons-header'>
        <h2>{t('lessonsHeading')}</h2>

        <button type='button' className='new-lesson-button' onClick={openNewLessonEditor}>
          <img src={plusIcon} alt='' aria-hidden='true' />
          {t('newLesson')}
        </button>
      </div> : null}

      {lessons.length === 0 ? <ErrorView
        icon={bookIcon}
        title={t('emptyTitle')}
        error={t('emptyMessage')}
        buttonText={t('newLesson')}
        buttonAction={openNewLessonEditor}
        fullHeight /> : <ol className='lesson-list'>
        {lessons.map((lesson) => {
          return <LessonRow
            key={lesson.id}
            lesson={lesson}
            onClick={() => {
              setLessonForEditor(lesson);
              setIsLessonEditorOpen(true);
            }}
          />
        })}
      </ol>}
      </>}
    </section>
  </div>
}

// A video's state is worth surfacing in the list: a still-processing or
// rejected upload isn't something a volunteer can watch yet.
function videoTagLabel(video: TrainingVideo) {
  switch (video.type) {
    case 'PROCESSING':
      return i18next.t('trainingModule:videoProcessing');
    case 'FAILED':
      return i18next.t('trainingModule:videoFailed');
    default:
      return i18next.t('trainingModule:video');
  }
}

function LessonRow(props: {
  lesson: TrainingResource;
  onClick: () => void;
}) {
  const attributes = props.lesson.attributes;
  const video = attributes.video ?? null;

  return <li>
    <button type='button' className='lesson-row' onClick={props.onClick}>
      <div className='lesson-thumbnail'>
        <img src={video?.thumbnailURL || lessonPlaceholder} alt='' aria-hidden='true' />
      </div>

      <div className='lesson-body'>
        <h3>{attributes.name}</h3>
        {attributes.description ? <p className='lesson-description'>{attributes.description}</p> : null}

        {video ? <div className='lesson-tags'>
          <span className={video.type === 'FAILED' ? 'lesson-tag failed' : 'lesson-tag'}>
            {videoTagLabel(video)}
          </span>
        </div> : null}
      </div>
    </button>
  </li>
}
